import { useState, useEffect, useRef } from "react";
import { Modal, Box, Button, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faSpinner, faCircleCheck, faMagnifyingGlass, faTriangleExclamation, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "./confirmationModal.js";
import KnowledgeLimitInfoModal from "./knowledgeLimitInfoModal.js";
import LinkBucketSection from "./LinkBucketSection.js";
import useLinkDiscovery from "../hooks/useLinkDiscovery.js";
import "../translations.js";
import "./addWebsiteLinkModal.css";

const AddWebsiteLinkModal = ({ open, onClose, onAddLinks, onRescrapeLinks, onRemoveLinks, botId, botUrl, existingLinks = [], remainingSlots = Infinity, onUpgrade }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState("crawl");
  const [urls, setUrls] = useState([""]);
  const [fetchUrl, setFetchUrl] = useState(botUrl || "");
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const initializedOnOpenRef = useRef(false);

  // Socket, request_id matching, timeout and the three buckets are shared with
  // the "Check for updates" dialog.
  const discovery = useLinkDiscovery({ botId, existingLinks });

  const resetState = () => {
    setActiveTab("crawl");
    setUrls([""]);
    setFetchUrl(botUrl || "");
    setConfirmRemoveOpen(false);
    discovery.reset();
  };

  useEffect(() => {
    if (open && !initializedOnOpenRef.current) {
      resetState();
      initializedOnOpenRef.current = true;
    }
    if (!open) {
      initializedOnOpenRef.current = false;
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, botUrl]);

  const handleUrlChange = (index, value) => {
    setUrls((prev) => prev.map((u, i) => (i === index ? value : u)));
  };

  const handleAddUrl = () => {
    setUrls((prev) => [...prev, ""]);
  };

  const handleRemoveUrl = (index) => {
    setUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFetchNewLinksCrawl = () => discovery.startCrawl(fetchUrl);

  const finishAndClose = () => {
    discovery.closeSocket();
    onClose();
    resetState();
  };

  const handleAddFromCrawl = () => {
    const merged = Array.from(new Set([...discovery.selectedNew]));
    if (merged.length === 0) return;
    if (merged.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    onAddLinks({
      urls: merged,
      bot_id: botId,
      user_id: localStorage.getItem("user_id"),
    });
    finishAndClose();
  };

  const handleRescrapeFromCrawl = () => {
    const merged = Array.from(new Set([...discovery.selectedExisting]));
    if (merged.length === 0) return;
    onRescrapeLinks({
      urls: merged,
      bot_id: botId,
      user_id: localStorage.getItem("user_id"),
    });
    finishAndClose();
  };

  const confirmRemoveFromCrawl = () => {
    if (discovery.selectedMissing.length === 0) return;
    setConfirmRemoveOpen(true);
  };

  const handleRemoveFromCrawl = () => {
    const merged = Array.from(new Set([...discovery.selectedMissing]));
    if (merged.length === 0) return;
    onRemoveLinks({
      urls: merged,
      bot_id: botId,
      user_id: localStorage.getItem("user_id"),
    });
    finishAndClose();
  };

  const handleAddFromManual = () => {
    const cleanedManual = urls.map((u) => u.trim()).filter(Boolean);
    const merged = Array.from(new Set([...cleanedManual]));
    if (merged.length === 0) return;
    if (merged.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    onAddLinks({
      urls: merged,
      bot_id: botId,
      user_id: localStorage.getItem("user_id"),
    });
    finishAndClose();
  };

  const canAddManual = urls.some(u => u.trim().length > 0);

  const { errorMsg, isFetching, expandedPanel, toggleExpand } = discovery;
  const hasAnyResult = discovery.newLinks.length > 0 || discovery.existing.length > 0 || discovery.missing.length > 0;

  return (
    <>
      <Modal open={open} onClose={() => { onClose(); }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: isMobile ? "90%" : "80%",
            maxWidth: 750,
            maxHeight: '88%',
            overflow: 'auto',
            outline: 'none'
          }}
        >
          <div className="tabs" style={{marginBottom: '20px '}}>
            <button className={activeTab === "crawl" ? "tab active" : "tab"} onClick={() => setActiveTab("crawl")}>{t("crawl_links")}</button>
            <button className={activeTab === "manual" ? "tab active" : "tab"} onClick={() => setActiveTab("manual")}>{t("manual_input")}</button>
          </div>

          {activeTab === "crawl" && (
            <>
              <h2 className="fnl-title">{t("crawl_new_links")}</h2>
              <Box className="fnl-row" display="flex" alignItems="flex-start" gap={2}>
                <TextField
                  fullWidth
                  label={t("website_link_sitemap")}
                  variant="outlined"
                  value={fetchUrl}
                  onChange={(e) => setFetchUrl(e.target.value)}
                  required
                  helperText={t("sitemap_hint") || "You can also enter a direct sitemap URL (e.g. https://example.com/sitemap.xml)"}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      border: "1px solid #515151",
                    },
                    "& .MuiInputLabel-root": {
                      color: "gray",
                      "&.Mui-focused": { color: "#5616ea" },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#888",
                      fontSize: "12px",
                      marginLeft: 0,
                    },
                  }}
                />
                <Button
                  onClick={handleFetchNewLinksCrawl}
                  variant="contained"
                  className="fnl-fetch-btn"
                  disabled={isFetching}
                  sx={{
                    backgroundColor: "#5616ea",
                    "&:hover": { backgroundColor: "#4a14d6" },
                    whiteSpace: "nowrap",
                    marginTop: "8px",
                  }}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} style={{marginRight: '8px'}} />{isFetching ? t("loading") : t("fetch")}
                </Button>
              </Box>

              {isFetching && (
                <div className="fnl-spinner-row">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t("crawl_in_progress_hint")}</span>
                </div>
              )}

              {(hasAnyResult || !!errorMsg) && (
                <div className="fnl-results">
                  {!!errorMsg && (
                    <div className="fnl-existing-summary fnl-error-summary">
                      {errorMsg}
                    </div>
                  )}

                  <LinkBucketSection
                    icon={faLightbulb}
                    title={t("new_found_links")}
                    links={discovery.newLinks}
                    selected={discovery.selectedNew}
                    expanded={expandedPanel === "new"}
                    onToggleExpand={(hasData) => toggleExpand("new", hasData)}
                    onToggleLink={discovery.toggleNewLink}
                    onSelectAll={discovery.selectAllNew}
                    action={{ label: t("add"), onClick: handleAddFromCrawl }}
                  />

                  <LinkBucketSection
                    icon={faCircleCheck}
                    title={t("links_already_trained") || "Links that are already trained"}
                    links={discovery.existing}
                    selected={discovery.selectedExisting}
                    expanded={expandedPanel === "existing"}
                    onToggleExpand={(hasData) => toggleExpand("existing", hasData)}
                    onToggleLink={discovery.toggleExistingLink}
                    onSelectAll={discovery.selectAllExisting}
                    action={{ label: t("rescrape") || "Rescrape", onClick: handleRescrapeFromCrawl }}
                  />

                  <LinkBucketSection
                    icon={faTriangleExclamation}
                    iconClassName="links-status-icon-warning"
                    title={t("links_not_existing_anymore") || "Links that are trained but do not exist anymore"}
                    links={discovery.missing}
                    selected={discovery.selectedMissing}
                    expanded={expandedPanel === "missing"}
                    onToggleExpand={(hasData) => toggleExpand("missing", hasData)}
                    onToggleLink={discovery.toggleMissingLink}
                    onSelectAll={discovery.selectAllMissing}
                    action={{ label: t("remove"), onClick: confirmRemoveFromCrawl }}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === "manual" && (
            <>
              <h2 className="fnl-title">{t("add_new_website_link")}</h2>
              {urls.map((url, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  className="fnl-row"
                >
                  <TextField
                    fullWidth
                    label={`${t("website_link")} ${urls.length > 1 ? idx + 1 : ""}`}
                    variant="outlined"
                    value={url}
                    onChange={(e) => handleUrlChange(idx, e.target.value)}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                        border: "1px solid #515151",
                      },
                      "& .MuiInputLabel-root": {
                        color: "gray",
                        "&.Mui-focused": { color: "#5616ea" },
                      },
                    }}
                  />
                  {urls.length > 1 && (
                    <button
                      onClick={() => handleRemoveUrl(idx)}
                      className="remove-url-button"
                      title={t("remove")}
                      style={{
                        marginLeft: 8,
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#9e9e9e",
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </Box>
              ))}
              <button
                onClick={handleAddUrl}
                className="add-url-button"
                title={t("add_link")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#5616ea",
                  margin: "2px 0 6px 0",
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                {t("add_link")}
              </button>
            </>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} className="fnl-actions">
            <Button
              onClick={finishAndClose}
              variant="outlined"
              className="fnl-cancel-btn"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" },
              }}
            >
              {t("cancel")}
            </Button>
            {activeTab === "manual" && (
              <Button
                onClick={handleAddFromManual}
                variant="contained"
                className="fnl-primary-btn"
                disabled={!canAddManual}
                sx={{
                  backgroundColor: "#5616ea",
                  "&:hover": { backgroundColor: "#4a14d6" },
                  "&.Mui-disabled": { backgroundColor: "#cfc7ff" },
                }}
              >
                {t("add")}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      <ConfirmationModal
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        onConfirm={handleRemoveFromCrawl}
        itemName={`${discovery.selectedMissing.length} ${t("selected")}`}
        requireTextConfirmation={false}
      />

      <KnowledgeLimitInfoModal
        open={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={() => { setLimitModalOpen(false); onUpgrade && onUpgrade(); }}
        remainingSlots={remainingSlots}
      />
    </>
  );
};

export default AddWebsiteLinkModal;
